import { TitleCasePipe } from '@angular/common';
import { ResultOutputValue } from '@ca/core/models';
import { TranslateService } from '@ngx-translate/core';
import clone from 'clone-deep';
import { isEqual } from '..';
import { ModuleInputType } from '../../app/core/enums/module-input-type.enum';
import { RunState } from '../../app/core/enums/run-state.enum';
import { StepState } from '../../app/core/enums/step-state.enum';
import { SystemModuleType } from '../../app/core/enums/system-module-type.enum';
import { WorkflowRunMode } from '../../app/core/enums/workflow-run-mode.enum';
import { ArchivedWorkflowRun } from '../../app/core/models/archived-workflow-run.model';
import { FileModel } from '../../app/core/models/file-model.model';
import { InteractiveProxyModule } from '../../app/core/models/interactive-proxy-module.model';
import { ListResult } from '../../app/core/models/list-result';
import { ModuleRunStatus } from '../../app/core/models/module-run-status.model';
import { ModuleV1Read } from '../../app/core/models/module-v1-read.model';
import { ModuleParameter, StepParameter } from '../../app/core/models/parameter.model';
import { PreviewResultModel } from '../../app/core/models/preview-result.model';
import { RunStep, StepInput } from '../../app/core/models/run-step.model';
import { Step } from '../../app/core/models/step.model';
import { WorkflowRunStatus } from '../../app/core/models/workflow-run-status.model';
import { WorkflowRunnerState } from '../../app/core/models/workflow-runner-state.model';
import { WorkflowNode } from '../../app/core/models/workflow-specification';
import { WorkflowV1Run } from '../../app/core/models/workflow-v1-read.model';
import { APEER_BLOB_STORAGE_URL_PART } from '../../app/shared/constants/constants';
import { convertKeyValuePairToStepParameter } from './domain-utils';

function bindSpecialParameterKeysForZenConnectorModule(key: string): string | null {
    if (key === 'var_env') {
        return window.location.origin;
    }
    if (key === 'var_token') {
        return localStorage.getItem('access_token');
    }

    return null;
}

export function getSelectedModules(workflow: WorkflowV1Run): ModuleV1Read[] {
    if (!workflow.specification || !workflow.specification.workflow) {
        return [];
    }
    return workflow.specification.workflow.flow
        .map((flowUnit: WorkflowNode) => flowUnit.run.module)
        .filter(moduleVersionId => moduleVersionId !== undefined)
        .map(moduleVersionId => {
            if (!workflow.usedModuleVersions) {
                throw new Error('getSelectedModules: Workflow has no valid module versions');
            }
            const selectedModuleVersion = workflow.usedModuleVersions.find(mv => mv.id === moduleVersionId);

            if (!selectedModuleVersion) {
                throw new Error('getSelectedModules: Module with ID already deleted');
            }

            const usedModule = workflow.usedModules ? workflow.usedModules.find(m => m.id === selectedModuleVersion.moduleId) : undefined;

            if (!usedModule) {
                // eslint-disable-next-line @typescript-eslint/no-base-to-string
                throw new Error(`getSelectedModules: Cannot find used module for ID ${selectedModuleVersion}`);
            }

            return new ModuleV1Read(usedModule, selectedModuleVersion);
        });
}

export function setModuleInputsFromRun(workflowRunArchive: ArchivedWorkflowRun, runSteps: RunStep[]): RunStep[] {
    // Use the inputs from the workflow run and set them instead of the default one from the workflow
    const runInputs = workflowRunArchive.inputs;
    return runSteps.map(step => {
        const newStep = { ...step };
        if (!newStep.module.selectedModuleVersion || !newStep.module.selectedModuleVersion.specification) {
            throw new Error('setModuleInputsFromRun: Cannot set input as module of step has no selected module versions');
        }

        // we need to sort as the order of the array returned by Object.entries() does not depend on how an object is defined
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sortedEntries = Object.entries(runInputs).sort((a: any, b: any) => a[0] - b[0]);

        if (newStep.module.systemModuleType != null) {
            for (const [flowId, value] of sortedEntries) {
                if (newStep.flowId === flowId) {
                    newStep.input = value as StepInput;

                    if (newStep.isSingleFileUploadModule && newStep.input.src_url && !Array.isArray(newStep.input.src_url)) {
                        newStep.input.src_url = [newStep.input.src_url];
                    }

                    if (!newStep.isSingleFileUploadModule) {
                        // Assign input values from workflow run to selected modules raw specification
                        // This is necessary as the ModuleParametersComponent parses the raw_specification property
                        Object.entries(newStep.module.selectedModuleVersion.specification.spec.inputs).forEach(entry => {
                            const [flowId, value] = entry;
                            value.default = [newStep.input[flowId], value.default, bindSpecialParameterKeysForZenConnectorModule(flowId)].find(
                                (item: undefined | null) => ![undefined, null].includes(item),
                            );
                        });
                    }
                }
            }
        }

        return newStep;
    });
}

export function getStepStatus(runSteps: RunStep[]): StepState[] {
    return runSteps.map(runStep => {
        switch (runStep.state) {
            case 'complete':
                return StepState.Complete;
            case 'waiting':
                return StepState.Waiting;
            case 'failed':
                return StepState.Failed;
            case 'running':
                return StepState.Running;
            case 'invalid-parameters':
                return StepState.InvalidParameters;
            default:
                return StepState.Idle;
        }
    });
}

export function isStepByStepIdle(interactiveProxyModules: InteractiveProxyModule[], flowIdOfActiveModule?: string): boolean {
    if (!interactiveProxyModules || interactiveProxyModules.length === 0) {
        return false;
    } else if (
        flowIdOfActiveModule != null &&
        interactiveProxyModules.filter((proxyModule: InteractiveProxyModule) => proxyModule.flowId === flowIdOfActiveModule).length === 0
    ) {
        return false;
    }
    return interactiveProxyModules.find((module: InteractiveProxyModule) => module.state === 'running') === undefined;
}

export function getWorkflowRunStatus(
    workflowRun: WorkflowRunStatus,
    translateService: TranslateService,
    titleCasePipe: TitleCasePipe,
    translationKeys: {
        runStatus: string;
        runFailedAtModule: string;
        runFailed: string;
        runFailedCompileError: string;
        finishedSuccessfully: string;
        runEnqueued: string;
        runQueryDelayed: string;
    },
): WorkflowRunnerState {
    let failureInformation: { workflowRunId?: string; module?: ModuleRunStatus; error?: string } = {};
    let message = '';

    if (workflowRun.modules.length) {
        const lastModule: ModuleRunStatus = workflowRun.modules[workflowRun.modules.length - 1];
        message = `Module ${lastModule.moduleId + 1} ${lastModule.state === 'failed' ? 'has' : 'is'} ${lastModule.state}`;

        if (lastModule.state === 'failed') {
            const failedModule: ModuleRunStatus | undefined = workflowRun.modules.find(module => module.state === 'failed');
            const failedModuleIndex: number | undefined = failedModule && failedModule.moduleId;
            message =
                failedModuleIndex !== undefined
                    ? translateService.instant(translationKeys.runFailedAtModule, { module: failedModuleIndex + 1 })
                    : translateService.instant(translationKeys.runFailedCompileError);
            failureInformation = { module: failedModule, workflowRunId: workflowRun.id };

            return {
                message,
                state: RunState.Failed,
                failureInformation,
                interactiveProxyModules: workflowRun.interactiveProxyModules,
            };
        }
    }

    if (['running', 'pending'].includes(workflowRun.state) && !workflowRun.modules.length) {
        message = translateService.instant(translationKeys.runStatus, { status: workflowRun.state });
    }

    if (workflowRun.state === 'failed') {
        message = translateService.instant(translationKeys.runFailed);
        failureInformation = { workflowRunId: workflowRun.id };
    }

    if (workflowRun.state === 'complete') {
        message = translateService.instant(translationKeys.finishedSuccessfully);
    }

    if (workflowRun.state === 'enqueued') {
        message = translateService.instant(translationKeys.runEnqueued);
    }

    if (workflowRun.state === 'Error: InternalServerError') {
        message = translateService.instant(translationKeys.runQueryDelayed);
    }

    return {
        message,
        state: RunState[titleCasePipe.transform(workflowRun.state)],
        failureInformation,
        interactiveProxyModules: workflowRun.interactiveProxyModules,
    };
}

/**
 * This method sets the RunInputs for the workflow.
 * We need to differentiate between a SingleRun and a BatchRun, and transform
 * the objects accordingly. In old workflows this is done by checking for 'src_url'.
 * For new workflows we do this by the flag isBatchRunForNewDefineInput (which does the check).
 */
export function setRunStepInputs(runSteps: RunStep[], isBatchRunForNewDefineInput: boolean = false): StepInput[] {
    const runStepInputs: StepInput[] = [];
    const starterStep: RunStep = runSteps[0];
    let fileUrl: string | undefined;

    if (starterStep.isSingleFileUploadModule) {
        // We create a input object for each entry in the src_url array
        // More than entry in this array means that we have a batch run
        if (typeof starterStep.input.src_url === 'string') {
            runStepInputs.push(getRunStepInputObject(runSteps, starterStep.input.src_url));
        } else {
            for (fileUrl of starterStep.input.src_url) {
                runStepInputs.push(getRunStepInputObject(runSteps, fileUrl));
            }
        }
    } else if (isBatchRunForNewDefineInput === true) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Object.entries(starterStep.input).forEach((entry: Array<any>) => {
            const value = entry[1];
            // It must be an array, since this is already checked when checking for batch run
            if (value != null && value !== '' && Array.isArray(value) && value.length > 0) {
                for (fileUrl of value) {
                    runStepInputs.push(getRunStepInputObject(runSteps, fileUrl, isBatchRunForNewDefineInput));
                }
            }
        });
    } else {
        runStepInputs.push(getRunStepInputObject(runSteps));
    }

    return runStepInputs;
}

export function updateStepsDisabledState(steps: RunStep[], runState: RunState, runMode?: WorkflowRunMode): RunStep[] {
    if (runMode === WorkflowRunMode.StepByStep) {
        // for step by step we need to handle disable separately. See RunnerComponent for details.
        return steps;
    }

    return steps.map((step: RunStep) => {
        step.disabled = runState !== RunState.Idle;
        return step;
    });
}

/**
 * ATTENTION: We should not update steps if input of any module changes
 * as this triggers a re-rendering of the step after every input change.
 */
export function getChangedSteps(oldSteps: RunStep[], newSteps: RunStep[]): RunStep[] {
    if (oldSteps.length === 0 && newSteps.length > 0) {
        return [];
    }

    const changedRunSteps: RunStep[] = [];

    for (const oldStep of oldSteps) {
        const newStep = newSteps.find((step: RunStep) => step.flowId === oldStep.flowId);

        if (newStep != null) {
            const stepFailed = oldStep.state !== 'failed' && newStep.state === 'failed';
            const invalidParameterStateChanged = oldStep.state !== 'invalid-parameters' && newStep.state === 'invalid-parameters';
            const disabledStatusChanged = oldStep.disabled !== newStep.disabled;
            const didResultsChange = didRunStepResultsChange(oldStep, newStep);

            if (stepFailed || invalidParameterStateChanged || didResultsChange || disabledStatusChanged) {
                changedRunSteps.push(newStep);
            }
        }
    }

    return changedRunSteps;
}

export function mapModulesParameterToWorkflowStep(moduleParameters: Object, workflowStep: Step | undefined): void {
    // Update module parameters to new ones
    if (!workflowStep || !moduleParameters) {
        return;
    }

    Object.entries(moduleParameters).forEach(keyValuePair => {
        const key: string = keyValuePair[0];
        const moduleParameter: ModuleParameter | undefined = workflowStep.moduleVersion.getInputParameter(key);

        // Exclude file and listFile from common parameter handling. They will be handled over inputFileInformations Array.
        if (
            moduleParameter == null ||
            (moduleParameter != null && moduleParameter.type !== ModuleInputType.File && moduleParameter.type !== ModuleInputType.ListFile)
        ) {
            const param: StepParameter | undefined = convertKeyValuePairToStepParameter(keyValuePair, workflowStep);

            if (param != null) {
                workflowStep.setInputParameter(param);
            }
        }
    });
}

function getRunStepInputObject(runSteps: RunStep[], fileUrl?: string, isBatchRunForNewDefineInput = false): Object {
    // Build input property for the request
    const inputObj = {};

    for (const runStep of runSteps) {
        let runStepInput = {};
        const isStarterStepASingleFileUploadModule = runStep.flowId === runSteps[0].flowId && runStep.isSingleFileUploadModule;

        if (isStarterStepASingleFileUploadModule) {
            runStepInput['src_url'] = fileUrl;
        } else if (runStep.isSingleFileUploadModule) {
            // Workaround for SingleFileUploader input field in module parameter form which can only output a string
            if (typeof runStep.input.src_url === 'string') {
                runStep.input.src_url = [runStep.input.src_url];
            }
            runStepInput['src_url'] = runStep.input.src_url[0];
        } else if (isBatchRunForNewDefineInput === true && runStep.index === 0) {
            // When it's a batch run, we need to clear file_list and put the URL which is passed
            // into file.
            runStepInput['file'] = fileUrl;
            runStepInput['file_list'] = [];
        } else {
            runStepInput = clone(runStep.input, true);

            // If it's a ModuleDefineInput or workflowDefineInput and it has a file parameter, it sometimes comes as array
            // (because previously we did not make the distinction for WorkflowDefineInput and ModuleDefineInput).
            // This is invalid, so we convert the array to a string here.
            if (
                (runStep.module.systemModuleType === SystemModuleType.DefineModuleInput ||
                    runStep.module.systemModuleType === SystemModuleType.DefineWorkflowInput) &&
                runStepInput['file'] != null &&
                Array.isArray(runStepInput['file'])
            ) {
                runStepInput['file'] = runStepInput['file'][0];
            }

            // 333719: Workflow Engine breaks when sending null for file parameters in DefineInput modules,
            // so we always need to send '' (if it's null)
            if (
                (runStep.module.systemModuleType === SystemModuleType.DefineModuleInput ||
                    runStep.module.systemModuleType === SystemModuleType.DefineWorkflowInput) &&
                runStepInput['file'] == null
            ) {
                runStepInput['file'] = '';
            }

            // Remove referenced inputs on workflow run
            // If operation_id is not null only then it means it is connection parameter.
            Object.keys(runStepInput)
                .filter(
                    key =>
                        runStepInput[key] != null &&
                        typeof runStepInput[key] === 'object' &&
                        runStepInput[key].operation_id != null &&
                        !Array.isArray(runStepInput[key]),
                )
                .forEach(key => delete runStepInput[key]);

            // This is a special case for the ZEN Connector module
            Object.keys(runStepInput)
                .filter(key => ['var_token', 'var_env'].includes(key))
                .forEach(key => (runStepInput[key] = bindSpecialParameterKeysForZenConnectorModule(key)));
        }

        inputObj[runStep.flowId] = runStepInput;
    }

    return inputObj;
}

function removeParametersFromResultUrl(runStep: RunStep): PreviewResultModel[] | undefined {
    if (!runStep.results) {
        return runStep.results;
    }
    return clone(runStep.results, true).map((result: PreviewResultModel) => {
        if (result.value && Array.isArray(result.value)) {
            result.value = result.value.map((v: string | number | FileModel) => {
                if (v instanceof FileModel && v.token != null) {
                    v.token = v.token.split('?')[0];
                    return v;
                } else {
                    return typeof v === 'string' ? v.split('?')[0] : v;
                }
            }) as ResultOutputValue;
        } else if (result.value != null) {
            if (result.value instanceof FileModel && result.value.token != null) {
                result.value.token = result.value.token.split('?')[0];
            } else if (typeof result.value === 'string') {
                result.value = result.value.split('?')[0];
            }
        }
        return result;
    });
}

function didRunStepResultsChange(oldStep: RunStep, newStep: RunStep): boolean {
    // It cannot happen that the results get back to 0 again.
    if (newStep.results && newStep.results.length === 0) {
        return false;
    }

    // Even if the results are the same (oldResult and newResult), the URLs come with different parameters (i.e. sas).
    // Therefore we need to cut all the parameters and then compare the results.
    let oldStepResultsWithoutParams = oldStep.results;
    let newStepResultsWithoutParams = newStep.results;

    if (oldStep.results != null) {
        oldStepResultsWithoutParams = removeParametersFromResultUrl(oldStep);
    }

    if (newStep.results != null) {
        newStepResultsWithoutParams = removeParametersFromResultUrl(newStep);
    }

    return !isEqual(oldStepResultsWithoutParams, newStepResultsWithoutParams);
}

export function isApeerFile(inputUrl?: string): boolean {
    let isFile = false;
    if (inputUrl != null && inputUrl.includes(APEER_BLOB_STORAGE_URL_PART)) {
        isFile = true;
    }

    return isFile;
}

export function isWebFile(inputUrl?: string): boolean {
    let isWeb = false;
    if (inputUrl != null && !isApeerFile(inputUrl)) {
        isWeb = true;
    }

    return isWeb;
}

export function setFilePaths(input: { src_url: string | string[]; file?: string | string[]; file_list?: string[] }): void {
    if (typeof input.src_url === 'string') {
        input.src_url = getFullFilePath(input.src_url);
    } else if (Array.isArray(input.src_url)) {
        for (let i = 0; i < input.src_url.length; i++) {
            input.src_url[i] = getFullFilePath(input.src_url[i]);
        }
    } else if (input.file != null && typeof input.file === 'string' && input.file !== '') {
        input.file = getFullFilePath(input.file);
    } else if (input.file && Array.isArray(input.file)) {
        for (let i = 0; i < input.file.length; i++) {
            input.file[i] = getFullFilePath(input.file[i]);
        }
    } else if (input.file_list) {
        for (let i = 0; i < input.file_list.length; i++) {
            input.file_list[i] = getFullFilePath(input.file_list[i]);
        }
    }
}

function getFullFilePath(path: string): string {
    return path;
}

/**
 * When running step by step modules, array contains modules for each iteration as flat list.
 * So we will get for each iteration one additional element in modules list.
 * But we are interested in the last iteration only because only then we can check the state
 * of the module properly e.g. if it is running or if it is completed.
 *
 */
export function getLatestExecutionModules(modules: ModuleRunStatus[]): ModuleRunStatus[] {
    const result: ModuleRunStatus[] = [];
    const lastExecutionModuleMap: {} = {};
    for (const module1 of modules) {
        if (lastExecutionModuleMap[module1.moduleId + '_' + module1.stepId]) {
            continue;
        }
        let lastExecutionModule: ModuleRunStatus = module1;
        for (const module2 of modules) {
            if (module1.moduleId === module2.moduleId && module1.stepId === module2.stepId) {
                const iteration1: number = module1.iteration != null ? module1.iteration : 0;
                const iteration2: number = module2.iteration != null ? module2.iteration : 0;
                if (iteration2 > iteration1) {
                    lastExecutionModule = module2;
                }
            }
        }
        lastExecutionModuleMap[lastExecutionModule.moduleId + '_' + lastExecutionModule.stepId] = true;
        result.push(lastExecutionModule);
    }
    return result;
}

export function getRunSteps(workflow: WorkflowV1Run, selectedModules: ModuleV1Read[], fileResult?: ListResult<FileModel>): RunStep[] {
    if (!workflow.specification || !workflow.specification.workflow) {
        throw Error('getRunSteps: Cannot build run steps without valid workflow');
    }

    const flow: WorkflowNode[] = workflow.specification.workflow.flow;

    if (flow.some((workflowNode: WorkflowNode) => !workflowNode.run)) {
        throw Error(`getRunSteps: Workflow node includes no run information: ${JSON.stringify(flow)}`);
    }

    return flow.map((workflowNode: WorkflowNode, index: number) => {
        const {
            run: { input, module: moduleRunId },
            id,
        } = workflowNode;

        const selectedModule: ModuleV1Read | undefined = selectedModules.find(
            m => m.selectedModuleVersion != null && m.selectedModuleVersion.id === moduleRunId,
        );

        if (!input) {
            throw Error('getRunSteps: Cannot build run steps without a valid input');
        }

        if (!selectedModule) {
            throw Error('getRunSteps: Cannot build run steps without a selected module');
        }

        const isSingleFileModule = isSingleFileUploadModule(selectedModule);
        const results: PreviewResultModel[] | undefined = getStepResults(index, fileResult);

        // The SingleFileUploader needs to have a url otherwise it is invalid
        if (isSingleFileModule && (!input.src_url || input.src_url.length === 0)) {
            input.src_url = [];
        }

        // some old workflows have a srcUrl string but we assume that src_url is always an array
        if (isSingleFileModule && input.src_url && typeof input.src_url === 'string') {
            input.src_url = [input.src_url];
        }

        // 335674: Safety check: file_list can never be empty (because then workflow engine crashes).
        // So in case we have a file parameter, we add a file_list with empty array.
        if (
            selectedModule.systemModuleType === SystemModuleType.DefineModuleInput ||
            selectedModule.systemModuleType === SystemModuleType.DefineWorkflowInput
        ) {
            if (input.file) {
                input.file_list = [];
            }
        }

        const runStep: RunStep = {
            index,
            flowId: id,
            input: input,
            module: selectedModule,
            isSingleFileUploadModule: isSingleFileModule,
            isSelectedInRunner: index === 0,
            results,
            disabled: false,
            isModuleParameterFormValid: true, // as we set default values they are always valid
        };

        return runStep;
    });
}

function isSingleFileUploadModule(module: ModuleV1Read): boolean {
    return module.title === 'Single File Upload';
}

function getStepResults(stepIndex: number, fileResult?: ListResult<FileModel>): PreviewResultModel[] {
    let results: PreviewResultModel[] = [];
    if (fileResult != null) {
        const files: FileModel[] = fileResult.data.filter((f: FileModel) => f?.workflowResultMetadata?.moduleStep === stepIndex.toString());

        if (files.length > 0) {
            results = [{ key: 'Results from previous run', value: files }];
        }
    }
    return results;
}
